<script setup lang="ts">
import { computed } from 'vue'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import { UsersIcon } from '@heroicons/vue/24/solid'

type TauxTier = { cumul: number; taux: number }

const props = defineProps<{
  data: { taux?: TauxTier[]; triggerMensuel?: number; objectifMarge?: number } | null
  memberMarge: number // marge individuelle du mois
  taux: number // taux équipe appliqué (reachedRate)
  part: number // part individuelle de la prime (variable)
  year: number
  reloadable?: boolean
}>()

const emit = defineEmits<{ (e: 'reload'): void }>()

const fmtEur = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`
const fmtPct = (t: number) => `${(t * 100).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`

const trigger = computed(() => props.data?.triggerMensuel ?? 0)
const objectif = computed(() => props.data?.objectifMarge ?? 0)
const tiers = computed(() => {
  const list = props.data?.taux
  return (Array.isArray(list) ? [...list] : []).sort((a, b) => a.cumul - b.cumul)
})
</script>

<template>
  <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <!-- En-tête -->
    <div class="flex justify-between items-start mb-4 flex-wrap gap-4">
      <div>
        <div class="text-xs uppercase tracking-wider font-semibold text-gray-500">Plan de commissionnement — Customer Success {{ year }}</div>
        <div class="flex items-baseline gap-3 mt-1">
          <span class="text-3xl font-extrabold text-gray-900">{{ fmtEur(part) }}</span>
          <span v-if="taux > 0" class="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm" :style="{ background: 'linear-gradient(135deg,#2dd4bf,#22d3ee)' }">
            Taux équipe : {{ fmtPct(taux) }}
          </span>
        </div>
        <div class="text-gray-500 text-sm uppercase tracking-wider font-semibold">Ta part de prime ce mois (répartie au prorata)</div>
      </div>
      <div class="flex flex-col items-end gap-2">
        <button
          v-if="reloadable"
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
          title="Recalculer le variable"
          @click="emit('reload')"
        >
          <ArrowPathIcon class="w-4 h-4" /> Recalculer
        </button>
        <div class="text-right">
          <span class="px-3 py-1 rounded-full text-xs font-bold text-white shadow" :style="{ background: 'linear-gradient(135deg,#2dd4bf,#f97316)' }">
            OBJECTIF MARGE ÉQUIPE
          </span>
          <div class="text-xl font-extrabold text-gray-800 mt-1">{{ fmtEur(objectif) }}</div>
        </div>
      </div>
    </div>

    <!-- Barème équipe -->
    <div class="flex flex-wrap gap-3 mb-5">
      <span class="px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm" :style="{ background: 'linear-gradient(135deg,#2dd4bf,#0ea5e9)' }">
        Seuil de déclenchement : {{ fmtEur(trigger) }} / mois (équipe)
      </span>
      <span
      <span
        v-for="(t, i) in tiers"
        :key="`${t.cumul}-${t.taux}`"
        class="px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm"
        :style="{ background: i === 0 ? 'linear-gradient(135deg,#34d399,#22d3ee)' : 'linear-gradient(135deg,#f59e0b,#f97316)' }"
      >
        {{ fmtPct(t.taux) }} {{ t.cumul > 0 ? `dès ${fmtEur(t.cumul)} cumulés` : 'par défaut' }}
      </span>
    </div>

    <!-- Stats du membre -->
    <div class="flex flex-wrap items-stretch gap-4">
      <div class="flex-1 min-w-0">
        <div class="bg-slate-50 rounded-lg p-5 border-2 border-slate-200 h-full">
          <div class="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <UsersIcon class="w-4 h-4" /> Ta marge du mois
          </div>
          <div class="text-2xl font-extrabold text-slate-900">{{ fmtEur(memberMarge) }}</div>
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <div class="bg-cyan-50 rounded-lg p-5 border-2 border-cyan-200 h-full">
          <div class="text-xs uppercase tracking-wider font-semibold text-cyan-700 mb-2">Taux équipe appliqué</div>
          <div class="text-2xl font-extrabold text-cyan-900">{{ fmtPct(taux) }}</div>
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <div class="bg-green-50 rounded-lg p-5 border-2 border-green-300 h-full">
          <div class="text-xs uppercase tracking-wider font-semibold text-green-700 mb-2">Ta part de prime</div>
          <div class="text-2xl font-extrabold text-green-900">{{ fmtEur(part) }}</div>
        </div>
      </div>
    </div>

    <p class="text-xs text-gray-500 mt-3">
      Prime collective : déclenchée dès {{ fmtEur(trigger) }} de marge équipe mensuelle. Le taux ({{ tiers.map((t) => fmtPct(t.taux)).join(' / ') }})
      dépend du cumul annuel de l'équipe, puis la prime est répartie au prorata de la production individuelle.
    </p>
  </div>
</template>

<style scoped></style>
